var _class;
import React, { Component } from 'react';
import { observer } from 'mobx-react';
import { getMeasurementMetric, getMeasurementSync } from '@kentik/ui-shared/query/measurements';
import { getDefaultRollupFn, getWindowFn, ROLLUP_OPTIONS } from '@kentik/ui-shared/query/query';
import { Box, Button, Flex, Text } from 'core/components';
import { formConsumer } from 'core/form';
import { SortableMenu } from 'app/views/boards/panels/ude/SortableMenu';
import MetricDimensionSelector from './MetricDimensionSelector';
const PRESET_PERCENTILES = new Set(ROLLUP_OPTIONS.filter(opt => opt.value.startsWith('p')).map(opt => opt.value));

// @ts-expect-error -- fix typing for formConsumer
let UdePanelMetrics = formConsumer(_class = observer(_class = class UdePanelMetrics extends Component {
  constructor(...args) {
    super(...args);
    this.state = {
      isAddingMetric: false
    };
    this.getRollupFnsForMetric = metricName => {
      const {
        form
      } = this.props;
      const rollups = form.getValue('config.data.rollups') || [];
      const fns = rollups.filter(r => r.metric === metricName).map(r => r.fn);
      return fns.length ? fns : ['avg'];
    };
    this.handleRollupChange = (metricName, newFns) => {
      const {
        form
      } = this.props;
      const currentRollups = form.getValue('config.data.rollups') || [];
      // Remove all existing rollups for this metric, then add one entry per selected fn
      const otherRollups = currentRollups.filter(r => r.metric !== metricName);
      const newEntries = newFns.map(fn => ({
        metric: metricName,
        fn
      }));
      form.setValue('config.data.rollups', [...otherRollups, ...newEntries]);

      // Also update window.fn so SQL query uses the correct aggregation function
      // Use the first selected function for the SQL window aggregation
      const primaryFn = newFns[0] || 'avg';
      const currentWindowFn = form.getValue('config.data.window.fn') || {};
      form.setValue('config.data.window.fn', {
        ...currentWindowFn,
        [metricName]: this.getWindowFunctionForMetric(metricName, primaryFn)
      });
    };
    this.handleRemoveMetric = metricName => {
      const {
        form
      } = this.props;
      const currentMetrics = form.getValue('config.data.metrics') || [];
      const updatedMetrics = currentMetrics.filter(metric => metric.name !== metricName);
      form.setValue('config.data.metrics', updatedMetrics);
      const currentRollups = form.getValue('config.data.rollups') || [];
      const updatedRollups = currentRollups.filter(rollup => rollup.metric !== metricName);
      form.setValue('config.data.rollups', updatedRollups);
      // Also clean up window.fn
      const currentWindowFn = form.getValue('config.data.window.fn') || {};
      const {
        [metricName]: _,
        ...updatedWindowFn
      } = currentWindowFn;
      form.setValue('config.data.window.fn', updatedWindowFn);
      // Clean up sort if it was sorting by this metric
      // Rollup format is fn_metric (e.g., "avg_latency"), extract metric portion for exact match
      const currentSort = form.getValue('config.data.sort') || [];
      const updatedSort = currentSort.filter(s =>
      // Rollup format is fn_metricName — but metric names can contain underscores,
      // so match against the known metric name at the end of the rollup string
      !s.rollup.endsWith(`_${metricName}`) && s.rollup !== metricName);
      form.setValue('config.data.sort', updatedSort);

      // Clean up metric filters that reference the removed metric
      // Use getField to access the form field state (not getValue which returns plain data)
      const filterGroups = form.getField('config.data.filters.metrics.filterGroups');
      if (filterGroups) {
        // Collect indices to remove (in reverse order to avoid index shifting issues)
        const groupsToRemove = [];
        filterGroups.forEach((group, groupIndex) => {
          // Collect filter indices to remove within this group
          const filtersToRemove = [];
          group.filters.fieldStates.forEach((filter, filterIndex) => {
            const filterField = filter.filterField.getValue();
            const metricField = filter.metric?.getValue?.() || '';
            if (filterField === metricName || metricField === metricName) {
              filtersToRemove.push(filterIndex);
            }
          });

          // Remove filters in reverse order to maintain correct indices
          filtersToRemove.reverse().forEach(filterIndex => {
            group.filters.remove(filterIndex);
          });

          // Check if group is now empty and should be removed
          const remainingFilters = group.filters.fieldStates?.length || 0;
          if (remainingFilters === 0) {
            groupsToRemove.push(groupIndex);
          }
        });

        // Remove empty groups in reverse order
        groupsToRemove.reverse().forEach(groupIndex => {
          filterGroups.remove(groupIndex);
        });
      }
    };
    this.handleOpenAddMetric = () => {
      this.setState({
        isAddingMetric: true
      });
    };
    this.handleCloseAddMetric = () => {
      this.setState({
        isAddingMetric: false
      });
    };
    this.handleAddMetric = metricValue => {
      const {
        form
      } = this.props;
      const currentMetrics = form.getValue('config.data.metrics') || [];
      if (!currentMetrics.find(metric => metric.name === metricValue)) {
        form.setValue('config.data.metrics', [...currentMetrics, {
          name: metricValue
        }]);

        // Check if rollup is needed from MEASUREMENTS_DATA
        const metricDetails = this.getMetricData(metricValue);
        if (metricDetails && !metricDetails.Aggregate) {
          const currentRollups = form.getValue('config.data.rollups') || [];
          if (!currentRollups.find(rollup => rollup.metric === metricValue)) {
            // Use appropriate default rollup function based on metric unit type
            const defaultFn = getDefaultRollupFn(metricDetails);
            form.setValue('config.data.rollups', [...currentRollups, {
              metric: metricValue,
              fn: defaultFn
            }]);

            // Also set window.fn so SQL query uses the correct aggregation
            // If the metric has a Window function defined, use that; otherwise use the default rollup
            const currentWindowFn = form.getValue('config.data.window.fn') || {};
            form.setValue('config.data.window.fn', {
              ...currentWindowFn,
              [metricValue]: this.getWindowFunctionForMetric(metricValue, defaultFn)
            });
          }
        }
      }
    };
    this.handleReorder = ({
      oldIndex,
      newIndex
    }) => {
      const {
        form
      } = this.props;
      const currentMetrics = form.getValue('config.data.metrics') || [];
      const updatedMetrics = Array.from(currentMetrics);
      const [movedMetric] = updatedMetrics.splice(oldIndex, 1);
      updatedMetrics.splice(newIndex, 0, movedMetric);
      form.setValue('config.data.metrics', updatedMetrics);
    };
  }
  get measurementData() {
    const {
      form,
      measurements
    } = this.props;
    const measurementName = form.getValue('config.data.measurement');
    if (measurementName) {
      return getMeasurementSync(measurementName, measurements);
    }
    return null;
  }
  getMetricData(metricName) {
    const {
      measurementData
    } = this;
    if (measurementData) {
      return getMeasurementMetric(measurementData, metricName);
    }
    return null;
  }
  getWindowFunctionForMetric(metricName, rollupFn) {
    return getWindowFn(this.getMetricData(metricName), rollupFn);
  }
  render() {
    const {
      form,
      measurements,
      appProtocols,
      headerVariant
    } = this.props;
    const {
      isAddingMetric
    } = this.state;
    const measurement = form.getValue('config.data.measurement');
    const metrics = form.getValue('config.data.metrics') || [];
    const dimensions = form.getValue('config.data.dimensions') || [];
    const lookback = form.getValue('config.data.time.lookback');
    const hasMeasurement = !!measurement;
    const allRollups = form.getValue('config.data.rollups') || [];
    const customPercentiles = [...new Set(allRollups.map(r => r.fn).filter(fn => fn.startsWith('p') && !PRESET_PERCENTILES.has(fn)))];
    const content = /*#__PURE__*/React.createElement(Flex, {
      flexDirection: "column",
      px: "12px",
      pt: 0,
      pb: 0,
      display: "flex",
      gap: 1
    }, !metrics?.length && /*#__PURE__*/React.createElement(Text, {
      muted: true,
      small: true,
      fontWeight: "normal"
    }, "No units selected"), metrics?.length > 0 && /*#__PURE__*/React.createElement(SortableMenu, {
      measurement: form.getValue('config.data.measurement') || '',
      handleRemoveItem: this.handleRemoveMetric,
      type: "metric",
      measurements: measurements,
      items: metrics,
      onSortEnd: this.handleReorder,
      getRollupFns: this.getRollupFnsForMetric,
      onRollupChange: this.handleRollupChange,
      customPercentiles: customPercentiles
    }));
    const addButton = hasMeasurement ? /*#__PURE__*/React.createElement(Box, {
      ml: "auto"
    }, /*#__PURE__*/React.createElement(MetricDimensionSelector, {
      type: "metric",
      measurement: measurement,
      measurements: measurements,
      selectedItems: metrics,
      selectedDimensions: dimensions,
      appProtocols: appProtocols,
      isOpen: isAddingMetric,
      onClose: this.handleCloseAddMetric,
      onSelect: this.handleAddMetric,
      onRemove: this.handleRemoveMetric,
      measurementLookback: lookback,
      keepOpenOnSelect: true
    }, ({
      isOpen
    }) => /*#__PURE__*/React.createElement(Button, {
      small: true,
      minimal: true,
      intent: "primary",
      text: "Add",
      onClick: this.handleOpenAddMetric,
      active: isOpen
    }))) : null;
    return headerVariant === 'pane' ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Flex, {
      alignItems: "center",
      gap: 1,
      p: "6px 8px 6px 16px"
    }, /*#__PURE__*/React.createElement(Text, {
      small: true,
      fontWeight: "bold",
      style: {
        textTransform: 'uppercase'
      }
    }, "Units"), addButton), content) : /*#__PURE__*/React.createElement(Flex, {
      flexDirection: "column",
      pb: "12px"
    }, /*#__PURE__*/React.createElement(Flex, {
      alignItems: "center",
      gap: 1,
      p: "6px 8px 6px 16px"
    }, /*#__PURE__*/React.createElement(Text, {
      small: true,
      fontWeight: "bold",
      style: {
        textTransform: 'uppercase'
      }
    }, "Units"), addButton), content);
  }
}) || _class) || _class;
export default UdePanelMetrics;